import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { fetchFastApi } from "../api/fastapi";
import {
  WorkspaceDocument,
  ListDocumentsResponseSchema,
  UploadResponseSchema,
  DownloadResponseSchema,
  MessageResponseSchema,
  UpdateDocumentRequest,
} from "../models/workspaceDocument";

interface UseWorkspaceDocumentsOptions {
  fetchOnLoad?: boolean;
}

export function useWorkspaceDocuments(
  options: UseWorkspaceDocumentsOptions = {},
) {
  const { fetchOnLoad = true } = options;

  const [loading, setLoading] = useState(fetchOnLoad);
  const [documents, setDocuments] = useState<WorkspaceDocument[]>([]);
  const [total, setTotal] = useState(0);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        path: "/workspace/documents",
        method: "GET",
      });

      const parsed = ListDocumentsResponseSchema.parse(response);
      setDocuments(parsed.documents);
      setTotal(parsed.total);
    } catch (error) {
      console.error("Error fetching workspace documents:", error);
      toast.error("Failed to load workspace documents");
    } finally {
      setLoading(false);
    }
  }, []);

  const uploadDocuments = useCallback(
    async (files: File[]): Promise<boolean> => {
      try {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append("files", file);
        });

        const response = await fetchFastApi({
          path: "/workspace/documents/upload",
          method: "POST",
          body: formData,
        });

        const parsed = UploadResponseSchema.parse(response);
        toast.success(parsed.message);

        // Refresh the list
        await fetchDocuments();

        return true;
      } catch (error) {
        console.error("Error uploading documents:", error);
        toast.error("Failed to upload documents");
        return false;
      }
    },
    [fetchDocuments],
  );

  const updateDocument = useCallback(
    async (
      documentId: string,
      data: UpdateDocumentRequest,
    ): Promise<boolean> => {
      try {
        const response = await fetchFastApi({
          path: `/workspace/documents/${documentId}`,
          method: "PATCH",
          body: data,
        });

        const parsed = MessageResponseSchema.parse(response);
        toast.success(parsed.message);

        // Update local state
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === documentId ? { ...doc, ...data } : doc,
          ),
        );

        return true;
      } catch (error) {
        console.error("Error updating document:", error);
        toast.error("Failed to update document");
        return false;
      }
    },
    [],
  );

  const deleteDocument = useCallback(
    async (documentId: string): Promise<boolean> => {
      try {
        const response = await fetchFastApi({
          path: `/workspace/documents/${documentId}`,
          method: "DELETE",
        });

        const parsed = MessageResponseSchema.parse(response);
        toast.success(parsed.message);

        // Remove from local state
        setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
        setTotal((prev) => prev - 1);

        return true;
      } catch (error) {
        console.error("Error deleting document:", error);
        toast.error("Failed to delete document");
        return false;
      }
    },
    [],
  );

  const getDownloadUrl = useCallback(
    async (documentId: string): Promise<string | null> => {
      try {
        const response = await fetchFastApi({
          path: `/workspace/documents/${documentId}/download`,
          method: "GET",
        });

        const parsed = DownloadResponseSchema.parse(response);
        return parsed.downloadUrl;
      } catch (error) {
        console.error("Error getting download URL:", error);
        toast.error("Failed to get download URL");
        return null;
      }
    },
    [],
  );

  const toggleActive = useCallback(
    async (documentId: string, isActive: boolean): Promise<boolean> => {
      return updateDocument(documentId, { isActive });
    },
    [updateDocument],
  );

  useEffect(() => {
    if (fetchOnLoad) {
      fetchDocuments();
    }
  }, [fetchOnLoad, fetchDocuments]);

  return {
    loading,
    documents,
    total,
    fetchDocuments,
    uploadDocuments,
    updateDocument,
    deleteDocument,
    getDownloadUrl,
    toggleActive,
  };
}
